import type { QueryClient } from '@tanstack/react-query'
import type { NetworkEnvironment } from '@/hooks/useNetworkEnvironment'
import {
  buildDashboardMetricsQueryOptions,
  ensureAdminCriticalQuery,
} from './queryOptions'

export const ADMIN_DEFERRED_NAVIGATION_TIMEOUT_MS = 12_000

export type AdminNavigationPath =
  | '/dashboard'
  | '/admin-users'
  | '/users'
  | '/merchants'
  | '/orders'
  | '/system'
  | '/monitoring'
  | '/rescue'
  | '/contract-management'
  | '/payment-management'
  | '/audit'
  | '/support'
  | '/announcements'
  | '/documentation-content'
  | '/account'

export type AdminExcludedNavigationPath =
  | '/'
  | '/login'
  | '/account/security'
  | '*'

export type AdminNavigationContext = {
  queryClient: QueryClient
  currentEnv: NetworkEnvironment
  isAuthenticated: boolean
  userId: string
  targetUrl: URL
}

export type AdminNavigationRoute = {
  path: AdminNavigationPath
  label: string
  mainnetOnly?: boolean
  preload: () => Promise<unknown>
  prepare: (context: AdminNavigationContext) => Promise<void>
}

export type AdminExcludedRoute = {
  path: AdminExcludedNavigationPath
  reason: 'redirect' | 'auth-transition' | 'fallback'
}

const routeCodeAlreadyLoaded = async () => {}
const prepareNoCriticalData = async () => {}

const prepareMainnetDashboard = async (context: AdminNavigationContext) => {
  if (!context.isAuthenticated || context.currentEnv !== 'mainnet') {
    return
  }

  await ensureAdminCriticalQuery(
    context.queryClient,
    buildDashboardMetricsQueryOptions(context.currentEnv)
  )
}

export const adminNavigationRoutes: readonly AdminNavigationRoute[] = [
  {
    path: '/dashboard',
    label: 'Dashboard',
    mainnetOnly: true,
    preload: routeCodeAlreadyLoaded,
    prepare: prepareMainnetDashboard,
  },
  {
    path: '/admin-users',
    label: 'Admin users',
    mainnetOnly: true,
    preload: routeCodeAlreadyLoaded,
    prepare: prepareNoCriticalData,
  },
  {
    path: '/users',
    label: 'Platform users',
    mainnetOnly: true,
    preload: routeCodeAlreadyLoaded,
    prepare: prepareNoCriticalData,
  },
  {
    path: '/merchants',
    label: 'Merchants',
    preload: routeCodeAlreadyLoaded,
    prepare: prepareNoCriticalData,
  },
  {
    path: '/orders',
    label: 'Orders',
    preload: routeCodeAlreadyLoaded,
    prepare: prepareNoCriticalData,
  },
  {
    path: '/system',
    label: 'System config',
    preload: routeCodeAlreadyLoaded,
    prepare: prepareNoCriticalData,
  },
  {
    path: '/monitoring',
    label: 'Monitoring',
    preload: routeCodeAlreadyLoaded,
    prepare: prepareNoCriticalData,
  },
  {
    path: '/rescue',
    label: 'Rescue',
    preload: routeCodeAlreadyLoaded,
    prepare: prepareNoCriticalData,
  },
  {
    path: '/contract-management',
    label: 'Contract management',
    preload: routeCodeAlreadyLoaded,
    prepare: prepareNoCriticalData,
  },
  {
    path: '/payment-management',
    label: 'Payment management',
    preload: routeCodeAlreadyLoaded,
    prepare: prepareNoCriticalData,
  },
  {
    path: '/audit',
    label: 'Audit logs',
    preload: routeCodeAlreadyLoaded,
    prepare: prepareNoCriticalData,
  },
  {
    path: '/support',
    label: 'Support',
    mainnetOnly: true,
    preload: routeCodeAlreadyLoaded,
    prepare: prepareNoCriticalData,
  },
  {
    path: '/announcements',
    label: 'Announcements',
    mainnetOnly: true,
    preload: routeCodeAlreadyLoaded,
    prepare: prepareNoCriticalData,
  },
  {
    path: '/documentation-content',
    label: 'Documentation content',
    preload: routeCodeAlreadyLoaded,
    prepare: prepareNoCriticalData,
  },
  {
    path: '/account',
    label: 'Account',
    preload: routeCodeAlreadyLoaded,
    prepare: prepareNoCriticalData,
  },
] as const

export const adminExcludedNavigationRoutes: readonly AdminExcludedRoute[] = [
  { path: '/', reason: 'redirect' },
  { path: '/login', reason: 'auth-transition' },
  { path: '/account/security', reason: 'redirect' },
  { path: '*', reason: 'fallback' },
] as const

export const adminDeferredNavigationPathSet = new Set(
  adminNavigationRoutes.map(route => route.path)
)

export const normalizeAdminPath = (
  pathname: string
): AdminNavigationPath | null => {
  const normalized = pathname !== '/' && pathname.endsWith('/')
    ? pathname.slice(0, -1)
    : pathname

  if (adminDeferredNavigationPathSet.has(normalized as AdminNavigationPath)) {
    return normalized as AdminNavigationPath
  }
  return null
}

export const findAdminNavigationRoute = (
  pathname: string
): AdminNavigationRoute | null => {
  const normalized = normalizeAdminPath(pathname)
  return adminNavigationRoutes.find(route => route.path === normalized) ?? null
}

export const adminNavigationInventorySummary = {
  deferred: adminNavigationRoutes.map(route => route.path),
  excluded: adminExcludedNavigationRoutes.map(route => ({
    path: route.path,
    reason: route.reason,
  })),
} as const
